// routes
import { COMPLIANCE_PATHS } from '../../routes/paths';
// icons
import outlineFactCheck from '@iconify/icons-ic/outline-fact-check';
import { Icon } from '@iconify/react';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CampaignIcon from '@mui/icons-material/Campaign';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import baselineContactSupport from '@iconify/icons-ic/baseline-contact-support';
import baselineSupportAgent from '@iconify/icons-ic/baseline-support-agent';
// redux
import { setResetPropertyPage } from 'src/redux/slices/Compliance/Property';
import { store } from 'src/redux/store';
// analytics
import { trackNavigationClick } from 'src/utils/analytics';
// ----------------------------------------------------------------------

// ENG-4211 — the case surfaces are tabs of one hub, so there is one Resources
// destination, not three.
//
// PA-10 — the entry is named after the product the user actually has. With the
// Assistant on it reads "Pronto Assistant", because a user looking for the Assistant
// should not have to guess it lives behind "Help and Resources". The path is
// unchanged, so every deep link and bookmark is untouched.
const complianceSidebarConfig = (
  supportHubEnabled,
  isSupportAgent,
  supportCasesEnabled = false
) => [
  {
    subheader: 'My Workbench',
    needsDashboard: true,
    items: [
      {
        title: 'My Renters',
        path: COMPLIANCE_PATHS.applicants.myApplicants,
        icon: <AssignmentIcon />,
        complianceManager: false,
        complianceSpecialist: true,
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.applicants.myApplicants,
            'my_renters'
          ),
      },
      {
        title: 'My Communications',
        path: COMPLIANCE_PATHS.user.communications,
        icon: <ContactMailOutlinedIcon />,
        complianceManager: false,
        complianceSpecialist: true,
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.user.communications,
            'my_communications'
          ),
      },
      {
        title: 'Bulk Communications',
        path: COMPLIANCE_PATHS.user.bulkCommunications,
        icon: <CampaignIcon />,
        complianceManager: true,
        complianceSpecialist: true,
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.user.bulkCommunications,
            'bulk_communications'
          ),
      },
      {
        title: 'Team Renters',
        path: COMPLIANCE_PATHS.applicants.teamApplicants,
        icon: <FamilyRestroomIcon />,
        complianceManager: true,
        complianceSpecialist: true,
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.applicants.teamApplicants,
            'team_renters'
          ),
      },
    ],
  },
  {
    subheader: 'My Work',
    needsDashboard: true,
    featureGate: 'canUseMyWorkBoard',
    items: [
      {
        title: 'My Work Board',
        path: COMPLIANCE_PATHS.applicants.myWork,
        icon: <ViewKanbanIcon />,
        matchPrefix: true,
        complianceManager: true,
        complianceSpecialist: true,
        onClick: () =>
          trackNavigationClick(COMPLIANCE_PATHS.applicants.myWork, 'my_work'),
      },
      {
        title: 'Workgroups',
        path: COMPLIANCE_PATHS.applicants.workgroups,
        icon: <GroupsIcon />,
        complianceManager: true,
        complianceSpecialist: false,
        featureGate: 'isWorkgroupManager',
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.applicants.workgroups,
            'workgroups'
          ),
      },
    ],
  },
  {
    subheader: 'Property Workbench',
    needsDashboard: false,
    items: [
      {
        title: 'Property Home',
        path: COMPLIANCE_PATHS.general.property,
        icon: <DashboardOutlinedIcon />,
        onClick: () => {
          store.dispatch(setResetPropertyPage(true));
          trackNavigationClick(
            COMPLIANCE_PATHS.general.property,
            'property_home'
          );
        },
        compliance: true,
      },
      {
        title: 'Certification Workbench',
        path: COMPLIANCE_PATHS.general.propertyCertWorkbench,
        icon: <Icon icon={outlineFactCheck} width={20} height={20} />,
        certificationW: true,
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.general.propertyCertWorkbench,
            'certification_workbench'
          ),
      },
      {
        title: 'Create Household',
        path: COMPLIANCE_PATHS.createQuestionnaire,
        icon: <GroupAddIcon />,
        compliance: true,
        onClick: () =>
          trackNavigationClick(
            COMPLIANCE_PATHS.createQuestionnaire,
            'create_household'
          ),
      },
    ],
  },
  ...(supportHubEnabled || isSupportAgent
    ? [
        {
          subheader: 'Resources',
          needsDashboard: false,
          items: [
            ...(supportHubEnabled
              ? [
                  {
                    title: supportCasesEnabled
                      ? 'Pronto Assistant'
                      : 'Help and Resources',
                    path: '/workbench/support/hub',
                    icon: (
                      <Icon
                        icon={baselineContactSupport}
                        width={20}
                        height={20}
                      />
                    ),
                    compliance: true,
                    onClick: () =>
                      trackNavigationClick(
                        '/workbench/support/hub',
                        'help_and_resources'
                      ),
                  },
                ]
              : []),
            // Agent console — Support group only. The route itself is wrapped
            // in RoleBasedGuard accessibleRoles={['Support']}; this keeps the
            // nav entry from advertising a page the user cannot open.
            ...(isSupportAgent
              ? [
                  {
                    title: 'Support Console',
                    path: '/workbench/support/dashboard',
                    icon: (
                      <Icon
                        icon={baselineSupportAgent}
                        width={20}
                        height={20}
                      />
                    ),
                    compliance: true,
                    onClick: () =>
                      trackNavigationClick(
                        '/workbench/support/dashboard',
                        'support_console'
                      ),
                  },
                ]
              : []),
          ],
        },
      ]
    : []),
];

export default complianceSidebarConfig;
